import { useCallback, useEffect, useRef, useState } from 'react'
import { layerAtomFamily } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type { Dimension } from '../data/v1'
import { useDesignerActions } from '../use-designer-actions'

const DRAG_EDGE = 4

function applyFloatDelta(dim: Dimension, deltaPx: number, canvasSizePx: number): Dimension {
	if (dim.mode === '%') {
		if (canvasSizePx <= 0) { return dim }
		const deltaPercent = (deltaPx / canvasSizePx) * 100
		return { ...dim, percentValue: Math.round((dim.percentValue + deltaPercent) * 100) / 100 }
	}
	return { ...dim, pxValue: Math.round(dim.pxValue + deltaPx) }
}

export function DragOverlay({ layerId, referenceEl }: { layerId: string; referenceEl: HTMLElement }) {
	const { updateLayer } = useDesignerActions()
	const store = useCanvasStore()
	const dragRef = useRef<
		{
			startX: number
			startY: number
			startFloatX: Dimension
			startFloatY: Dimension
			canvasWidth: number
			canvasHeight: number
		} | null
	>(null)

	const handlePointerDown = useCallback((e: React.PointerEvent) => {
		e.preventDefault()
		e.stopPropagation()
		;(e.target as HTMLElement).setPointerCapture(e.pointerId)

		const layer = store.get(layerAtomFamily(layerId))
		if (!layer || !('floating' in layer) || !layer.floating) { return }

		const canvasEl = referenceEl.closest<HTMLElement>('[style*="--canvas-width"]')
		const canvasWidth = canvasEl ? parseFloat(getComputedStyle(canvasEl).getPropertyValue('--canvas-width')) : 0
		const canvasHeight = canvasEl ? parseFloat(getComputedStyle(canvasEl).getPropertyValue('--canvas-height')) : 0

		dragRef.current = {
			startX: e.clientX,
			startY: e.clientY,
			startFloatX: layer.floatX,
			startFloatY: layer.floatY,
			canvasWidth,
			canvasHeight,
		}

		document.body.style.cursor = 'grabbing'
		document.body.style.userSelect = 'none'
	}, [layerId, store, referenceEl])

	const handlePointerMove = useCallback((e: React.PointerEvent) => {
		const drag = dragRef.current
		if (!drag) { return }

		const dx = e.clientX - drag.startX
		const dy = e.clientY - drag.startY

		const floatX = applyFloatDelta(drag.startFloatX, dx, drag.canvasWidth)
		const floatY = applyFloatDelta(drag.startFloatY, dy, drag.canvasHeight)

		updateLayer(layerId, { floatX, floatY }, { transient: true })
	}, [layerId, updateLayer])

	const finishDrag = useCallback(() => {
		if (!dragRef.current) { return }
		dragRef.current = null
		document.body.style.cursor = ''
		document.body.style.userSelect = ''
	}, [])

	const handlePointerUp = useCallback((e: React.PointerEvent) => {
		if (!dragRef.current) { return }
		try {
			;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
		} catch { /* noop */ }
		finishDrag()

		const layer = store.get(layerAtomFamily(layerId))
		if (layer && 'floatX' in layer) {
			updateLayer(layerId, { floatX: layer.floatX, floatY: layer.floatY })
		}
	}, [layerId, store, updateLayer, finishDrag])

	useEffect(() => finishDrag, [finishDrag])

	const [hovered, setHovered] = useState(false)

	const edgeProps = {
		onPointerDown: handlePointerDown,
		onPointerMove: handlePointerMove,
		onPointerUp: handlePointerUp,
		onPointerCancel: handlePointerUp,
		onPointerEnter: () => setHovered(true),
		onPointerLeave: () => setHovered(false),
	}

	const edgeBase: React.CSSProperties = {
		position: 'absolute',
		pointerEvents: 'auto',
		cursor: 'grab',
	}

	return (
		<>
			{/* full-frame ring highlight (click-through) */}
			<div
				style={{
					position: 'absolute',
					inset: -1,
					border: hovered ? '3px solid rgba(59, 130, 246, 0.35)' : '3px solid transparent',
					transition: 'border-color 150ms ease-out',
					pointerEvents: 'none',
				}}
			/>
			{/* top */}
			<div
				style={{ ...edgeBase, top: -DRAG_EDGE, left: -DRAG_EDGE, right: -DRAG_EDGE, height: DRAG_EDGE * 2 }}
				{...edgeProps}
			/>
			{/* bottom */}
			<div
				style={{ ...edgeBase, bottom: -DRAG_EDGE, left: -DRAG_EDGE, right: -DRAG_EDGE, height: DRAG_EDGE * 2 }}
				{...edgeProps}
			/>
			{/* left */}
			<div
				style={{ ...edgeBase, top: DRAG_EDGE, bottom: DRAG_EDGE, left: -DRAG_EDGE, width: DRAG_EDGE * 2 }}
				{...edgeProps}
			/>
			{/* right */}
			<div
				style={{ ...edgeBase, top: DRAG_EDGE, bottom: DRAG_EDGE, right: -DRAG_EDGE, width: DRAG_EDGE * 2 }}
				{...edgeProps}
			/>
		</>
	)
}
